import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/shared/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Phone, Car as CarIcon, ArrowRight } from "lucide-react";

/**
 * /admin/dashboard — Drivers + raw routes/users/trips ledger. Uses the
 * same Swiss editorial system as Control Room: cream container, hairline
 * separators, orange label-xs micro-headlines, navy serif titles, and
 * pill-shaped status badges. AdminTopNav docks at the top so the operator
 * never lands here without a way home.
 */
export default function AdminDashboard() {
  const [tab, setTab] = useState("routes");
  const [users, setUsers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [dName, setDName] = useState("");
  const [dPhone, setDPhone] = useState("");
  const [dVehicle, setDVehicle] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [u, r, t, d] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/routes"),
        api.get("/admin/trips"),
        api.get("/admin/drivers"),
      ]);
      setUsers(u.data); setRoutes(r.data); setTrips(t.data); setDrivers(d.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addDriver = async (e) => {
    e.preventDefault();
    if (!dName || !dPhone) return;
    setBusy(true);
    try {
      await api.post("/admin/drivers", { name: dName, phone: dPhone, vehicle: dVehicle });
      setDName(""); setDPhone(""); setDVehicle("");
      toast.success("Driver added"); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const assign = async (routeId, driverId) => {
    try {
      await api.post(`/admin/routes/${routeId}/assign-driver`, { driver_id: driverId || null });
      toast.success("Driver assignment updated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Assignment failed");
    }
  };

  const setStatus = async (routeId, status) => {
    try {
      await api.post(`/admin/routes/${routeId}/status`, { status });
      toast.success(`Route ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Status change failed");
    }
  };

  const tabs = [
    { id: "routes",  label: "Routes",  count: routes.length },
    { id: "users",   label: "Riders",  count: users.length  },
    { id: "trips",   label: "Trips",   count: trips.length  },
    { id: "drivers", label: "Drivers", count: drivers.length },
  ];

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="admin-dashboard">
        <header className="pb-5 mb-6 border-b hairline">
          <div className="label-xs text-[#ed6500]">Link · Operators</div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-none mt-2">Dispatch & roster.</h1>
          <p className="text-sm text-[#515E7B] mt-2">
            Assign drivers, walk the trip ledger, manage the team.
          </p>
        </header>

        <div className="flex gap-1 sm:gap-2 border-b hairline mb-6 overflow-x-auto" data-testid="admin-dashboard-tabs">
          {tabs.map(({ id, label, count }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-1.5 px-3 py-3 sm:py-3.5 text-[10px] sm:text-[11px] tracking-[0.2em] uppercase font-semibold whitespace-nowrap transition-colors ${
                tab === id ? "text-[#ed6500] border-b-2 border-[#ed6500] -mb-px" : "text-[#515E7B] hover:text-[#0A1128]"
              }`}
              data-testid={`admin-tab-${id}`}
              data-selected={tab === id}
            >
              <span>{label}</span>
              <span className={`text-[9px] tabular-nums ${tab === id ? "text-[#ed6500]/80" : "text-[#515E7B]/60"}`}>· {count}</span>
            </button>
          ))}
        </div>

        {loading && (
          <div className="py-16 flex items-center justify-center" data-testid="admin-dashboard-loading">
            <Loader2 size={20} className="animate-spin text-[#515E7B]" />
          </div>
        )}

        {!loading && tab === "routes" && (
          <section className="border hairline bg-white overflow-x-auto" data-testid="admin-routes-table">
            {routes.length === 0 ? (
              <EmptyState>No routes yet.</EmptyState>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F9F7F4]">
                  <tr className="label-xs text-[#515E7B]">
                    <th className="text-left p-4">Rider</th>
                    <th className="text-left p-4">Route</th>
                    <th className="text-left p-4">Days · Times</th>
                    <th className="text-left p-4">Plan</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Driver</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E1D8]">
                  {routes.map((r) => (
                    <tr key={r.id} data-testid={`admin-route-${r.id}`} className="hover:bg-[#F9F7F4]/50 transition-colors">
                      <td className="p-4 align-top">
                        <div className="font-medium text-[#0A1128]">{r.user_name}</div>
                        <div className="text-[11px] text-[#515E7B]">{r.user_email}</div>
                      </td>
                      <td className="p-4 max-w-xs align-top">
                        <div className="truncate text-[#0A1128]">{r.pickup_address}</div>
                        <div className="text-[#515E7B] truncate">→ {r.destination_address}</div>
                      </td>
                      <td className="p-4 align-top">
                        <div className="text-[#0A1128]">{r.days.join(" · ")}</div>
                        <div className="text-[11px] text-[#515E7B] mt-0.5">out {r.pickup_time} / ret {r.return_time}</div>
                      </td>
                      <td className="p-4 align-top">
                        <div className="text-[10px] tracking-widest uppercase font-semibold text-[#0A1128]">{r.tier_id}</div>
                        <div className="text-[10px] text-[#515E7B] mt-0.5 capitalize">{r.billing_cycle} · ${r.cycle_amount?.toFixed(0)}</div>
                      </td>
                      <td className="p-4 align-top">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="p-4 align-top">
                        <select
                          className="border hairline bg-white px-2 py-1.5 text-xs text-[#0A1128] focus:outline-none focus:border-[#ed6500]"
                          value={r.driver_id || ""}
                          onChange={(e) => assign(r.id, e.target.value)}
                          data-testid={`assign-driver-${r.id}`}
                        >
                          <option value="">Unassigned</option>
                          {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      </td>
                      <td className="p-4 align-top">
                        {r.status !== "cancelled" && r.status !== "pending_payment" && (
                          <button
                            className="text-[10px] tracking-widest uppercase font-semibold text-[#ed6500] hover:text-[#d65a00] transition-colors"
                            onClick={() => setStatus(r.id, r.status === "active" ? "paused" : "active")}
                            data-testid={`admin-toggle-${r.id}`}
                          >
                            {r.status === "active" ? "Pause" : "Resume"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        {!loading && tab === "users" && (
          <section className="border hairline bg-white overflow-x-auto" data-testid="admin-users-table">
            {users.length === 0 ? (
              <EmptyState>No riders yet.</EmptyState>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F9F7F4]">
                  <tr className="label-xs text-[#515E7B]">
                    <th className="text-left p-4">Name</th>
                    <th className="text-left p-4">Email</th>
                    <th className="text-left p-4">Role</th>
                    <th className="text-left p-4">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E1D8]">
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-${u.id}`} className="hover:bg-[#F9F7F4]/50 transition-colors">
                      <td className="p-4 text-[#0A1128]">{u.name}</td>
                      <td className="p-4 text-[#515E7B]">{u.email}</td>
                      <td className="p-4"><RolePill role={u.role} /></td>
                      <td className="p-4 text-[11px] text-[#515E7B]">{new Date(u.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        {!loading && tab === "trips" && (
          <section className="border hairline bg-white overflow-x-auto" data-testid="admin-trips-table">
            {trips.length === 0 ? (
              <EmptyState>No upcoming trips.</EmptyState>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F9F7F4]">
                  <tr className="label-xs text-[#515E7B]">
                    <th className="text-left p-4">Date</th>
                    <th className="text-left p-4">Time</th>
                    <th className="text-left p-4">Direction</th>
                    <th className="text-left p-4">From → To</th>
                    <th className="text-left p-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E1D8]">
                  {trips.map((t) => (
                    <tr key={t.id} data-testid={`admin-trip-${t.id}`} className="hover:bg-[#F9F7F4]/50 transition-colors">
                      <td className="p-4 text-[#0A1128]">{t.date}</td>
                      <td className="p-4 font-serif text-base text-[#0A1128]">{t.pickup_time}</td>
                      <td className="p-4 text-[10px] tracking-widest uppercase text-[#515E7B]">{t.direction}</td>
                      <td className="p-4 max-w-lg">
                        <div className="truncate text-[#515E7B]">{t.pickup_address} → {t.dropoff_address}</div>
                      </td>
                      <td className="p-4"><StatusPill status={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        {!loading && tab === "drivers" && (
          <section className="grid lg:grid-cols-3 gap-6 lg:gap-10" data-testid="admin-drivers-section">
            <form onSubmit={addDriver} className="bg-white border hairline p-5 lg:col-span-1 space-y-5" data-testid="add-driver-form">
              <div className="label-xs text-[#ed6500]">Add driver</div>
              <input
                className="input-line"
                placeholder="Name"
                value={dName}
                onChange={(e) => setDName(e.target.value)}
                data-testid="driver-name"
              />
              <input
                className="input-line"
                placeholder="Phone"
                value={dPhone}
                onChange={(e) => setDPhone(e.target.value)}
                data-testid="driver-phone"
              />
              <input
                className="input-line"
                placeholder="Vehicle (e.g. 2023 Toyota Camry)"
                value={dVehicle}
                onChange={(e) => setDVehicle(e.target.value)}
                data-testid="driver-vehicle"
              />
              <button
                type="submit"
                disabled={busy || !dName || !dPhone}
                className="w-full bg-[#ed6500] text-white py-3 text-[10px] tracking-widest uppercase font-semibold hover:bg-[#d65a00] disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="submit-driver"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Add driver
              </button>
            </form>

            <div className="lg:col-span-2 border hairline bg-white" data-testid="admin-drivers-list">
              {drivers.length === 0 ? (
                <EmptyState>No drivers yet. Add the first one to start dispatching.</EmptyState>
              ) : (
                <ul className="divide-y divide-[#E5E1D8]">
                  {drivers.map((d) => (
                    <li
                      key={d.id}
                      className="p-4 sm:p-5 flex items-center justify-between gap-3 hover:bg-[#F9F7F4]/50 transition-colors"
                      data-testid={`driver-row-${d.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-serif text-base sm:text-lg text-[#0A1128] truncate">{d.name}</div>
                        <div className="text-[11px] text-[#515E7B] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          <span className="inline-flex items-center gap-1"><Phone size={10} strokeWidth={1.8} /> {d.phone || "—"}</span>
                          <span className="inline-flex items-center gap-1"><CarIcon size={10} strokeWidth={1.8} /> {d.vehicle || "—"}</span>
                        </div>
                      </div>
                      <ArrowRight size={14} className="text-[#515E7B]/50 shrink-0" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

function EmptyState({ children }) {
  return (
    <div className="p-8 sm:p-12 text-center text-[#515E7B] text-sm">
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  // Color system shared with /admin/ops:
  //   active / completed → sky blue (positive)
  //   paused / scheduled → orange (action required)
  //   cancelled / failed → muted slate
  const map = {
    active:           { bg: "bg-[#0074d4]/10", fg: "text-[#0074d4]" },
    completed:        { bg: "bg-[#0074d4]/10", fg: "text-[#0074d4]" },
    paused:           { bg: "bg-[#ed6500]/10", fg: "text-[#ed6500]" },
    pending_payment:  { bg: "bg-[#ed6500]/10", fg: "text-[#ed6500]" },
    scheduled:        { bg: "bg-[#ed6500]/10", fg: "text-[#ed6500]" },
    cancelled:        { bg: "bg-[#515E7B]/10", fg: "text-[#515E7B]" },
    failed:           { bg: "bg-[#515E7B]/10", fg: "text-[#515E7B]" },
  };
  const tone = map[status] || { bg: "bg-[#515E7B]/10", fg: "text-[#515E7B]" };
  return (
    <span className={`inline-block px-2 py-1 ${tone.bg} ${tone.fg} text-[9.5px] tracking-[0.18em] uppercase font-semibold whitespace-nowrap`}>
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

function RolePill({ role }) {
  const map = {
    admin:  { bg: "bg-[#ed6500]/10", fg: "text-[#ed6500]" },
    driver: { bg: "bg-[#0074d4]/10", fg: "text-[#0074d4]" },
    user:   { bg: "bg-[#515E7B]/10", fg: "text-[#515E7B]" },
  };
  const tone = map[role] || map.user;
  return (
    <span className={`inline-block px-2 py-0.5 ${tone.bg} ${tone.fg} text-[9.5px] tracking-[0.18em] uppercase font-semibold`}>
      {role || "user"}
    </span>
  );
}
